import cn from 'classnames';
import {
    Program as PlanbyProgram,
    ProgramBox,
    ProgramContent,
    ProgramFlex,
    ProgramItem,
    useProgram,
    Channel,
} from 'planby';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { EpgContainerRefContext } from '../EPG';
import channelRowStyles from './channel-row.module.sass';
import useDragging from './hooks/useDragging';
import useProgramsWithLiveStatus from './hooks/useProgramsWithLiveStatus';
import useChannelRowScroll from './hooks/useChannelRowScroll';
import ProgramDetailsModal from './ProgramDetailsModal';
import ProgramContainer from './ProgramContainer';

interface ChannelRowProps {
    programs: PlanbyProgram[];
    dayWidth: number;
    onProgramSelect: (program: PlanbyProgram, channel?: Channel) => void;
    selectedProgram: PlanbyProgram | undefined;
    lang: string;
    liveTag: string;
    beforeTag: string;
    afterTag: string;
    isReminderSet?: boolean;
    isFavorite?: boolean | undefined | any;
    onFavoriteClick?: (channelId: string) => void;
    epgLocalization: any;
    AddWatchListButton?: any;
    AddReminderButton?: any;
    reminderData?: any[];
    excludeIdFromUrl?: any;
    date?: any;
    onPPVPurchaseClick?: (eventId: string) => void;
    // PPV configuration for pricing
    ppvEventConfig?: any;
    operators?: any;
    fetchPreviousProgramOnScroll?: (channelId: any) => void;
    fetchFutureProgramOnScroll?: (channelId: any) => void;
}

// Memoized options to prevent recreation on every render
const USE_IN_VIEW_OPTIONS = {
    rootMargin: '600px 0px',
};

const ChannelRowComponent = ({
    program,
    dayWidth,
    onProgramSelect,
    selectedProgram,
    lang,
    liveTag,
    beforeTag,
    afterTag,
    isRTL,
    isFavorite: initialIsFavorite,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onFavoriteClick = () => {}, // Default to a no-op function
    epgLocalization,
    AddWatchListButton,
    AddReminderButton,
    // TODO: move reminderData/excludeIdFromUrl shouldn't be passed as props? It looks like they're used to check
    // whether reminder is set. It might be better to do this on WCP side and add isReminded flag to program
    reminderData,
    excludeIdFromUrl,
    date,
    onPPVPurchaseClick,
    ppvEventConfig,
    operators,
    fetchFutureProgramOnScroll,
    fetchPreviousProgramOnScroll,
    ...rest
}: ChannelRowProps & ProgramItem) => {
    // the EPG scroller, so rootMargin pre-renders rows before they scroll into view instead of at its edge
    const root = useContext(EpgContainerRefContext)?.current ?? null;
    // TODO: React's <Activity> API maybe?
    const { ref, inView } = useInView({ ...USE_IN_VIEW_OPTIONS, root });

    const { styles, formatTime, set12HoursTimeFormat } = useProgram({
        program,
        ...rest,
    });

    const { programsWithStatus } = useProgramsWithLiveStatus(rest.programs);
    const programRefs = useRef(new Map());
    const programContainer = useRef<HTMLDivElement | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<any>(null);

    const channelRowScrollProps: any = {
        date,
        inView,
        isRTL,
        program,
        selectedProgram,
        programsWithStatus,
        programContainer,
        programRefs,
        fetchPreviousProgramOnScroll,
        fetchFutureProgramOnScroll,
    };

    useChannelRowScroll(channelRowScrollProps);

    useDragging(programContainer.current, programRefs.current, inView);

    const handleModalOpen = useCallback((content: any) => {
        setModalContent(content);
        setIsModalOpen(true);
    }, []);

    const handleOpenChange = useCallback((isOpen?: boolean) => {
        setIsModalOpen(!!isOpen);
    }, []);

    return (
        <>
            <ProgramBox
                ref={(el) => {
                    ref(el);
                    programContainer.current = el;
                }}
                width={dayWidth}
                style={{ ...styles.position }}
                className={cn(channelRowStyles.box)}
                // onEnded={(e) => {
                //     console.log('testing =======', e);
                // }}
            >
                {inView ? (
                    <ProgramContent
                        // `width` and `isLive` are not needed?
                        width={styles.width}
                        isLive={false}
                        className={cn(channelRowStyles.program)}
                    >
                        <ProgramFlex className={channelRowStyles.flex}>
                            {programsWithStatus.map((p) => {
                                return (
                                    <ProgramContainer
                                        key={`${p.id}-${p.since}-${p.till}`}
                                        program={p}
                                        reminderData={reminderData}
                                        excludeIdFromUrl={excludeIdFromUrl}
                                        selectedProgram={selectedProgram}
                                        programRefs={programRefs}
                                        onProgramSelect={onProgramSelect}
                                        lang={lang}
                                        formatTime={formatTime}
                                        liveTag={liveTag}
                                        beforeTag={beforeTag}
                                        afterTag={afterTag}
                                        set12HoursTimeFormat={set12HoursTimeFormat}
                                        onFavoriteClick={onFavoriteClick}
                                        initialIsFavorite={initialIsFavorite}
                                        onModalOpenClick={handleModalOpen}
                                    />
                                );
                            })}
                        </ProgramFlex>
                    </ProgramContent>
                ) : null}
            </ProgramBox>

            <ProgramDetailsModal
                isOpen={isModalOpen}
                onOpenChange={handleOpenChange}
                selectedProgram={selectedProgram}
                modalContent={modalContent}
                ppvEventConfig={ppvEventConfig}
                onPPVPurchaseClick={onPPVPurchaseClick}
                onFavoriteClick={onFavoriteClick}
                initialIsFavorite={initialIsFavorite}
                favoriteButtonLabel={epgLocalization.favorite_button_label}
                removeFavoriteButtonLabel={epgLocalization.remove_favorite_button_label}
                beforeTag={beforeTag}
                afterTag={afterTag}
                liveTag={liveTag}
                AddReminderButton={AddReminderButton}
                AddWatchListButton={AddWatchListButton}
                lang={lang}
            />
        </>
    );
};

export const ChannelRow = React.memo(ChannelRowComponent);
